import { useState, useRef, useEffect, useCallback } from 'react'
import { Send, Paperclip, Image as ImageIcon, Smile, X, Clock } from 'lucide-react'
import EmojiPicker, { EmojiClickData } from 'emoji-picker-react'
import { cannedResponsesApi, CannedResponse, resolveCannedVars } from '@/services/cannedResponsesApi'

export interface MessageAttachment {
  id: string
  type: 'image' | 'file'
  file: File
  preview?: string // data URL para imágenes
  base64?: string // base64 sin prefijo data:
}

export interface MessageComposerProps {
  onSendText: (text: string, scheduledAt?: string) => void
  onSendImage: (file: File, base64: string, caption?: string) => void
  onSendFile: (file: File, base64: string, caption?: string) => void
  disabled?: boolean
  placeholder?: string
  prefillText?: string | null
  prefillVersion?: number
  focusTrigger?: number
  onTyping?: (isTyping: boolean) => void
  cannedVars?: {
    nombre_cliente?: string
    nombre_tienda?: string
    numero_cliente?: string
  }
}

export function MessageComposer({
  onSendText,
  onSendImage,
  onSendFile,
  disabled = false,
  placeholder = 'Escribe un mensaje',
  prefillText = null,
  prefillVersion = 0,
  focusTrigger = 0,
  onTyping,
  cannedVars,
}: MessageComposerProps) {
  const MAX_MESSAGE_LENGTH = 65536
  const [text, setText] = useState(prefillText ? prefillText.trim() : '')
  const [attachments, setAttachments] = useState<MessageAttachment[]>([])
  const [isSending, setIsSending] = useState(false)
  const [showEmojiPicker, setShowEmojiPicker] = useState(false)
  const [showSchedulePicker, setShowSchedulePicker] = useState(false)
  const [scheduledAt, setScheduledAt] = useState<string>('')

  // Canned responses slash menu
  const [cannedResponses, setCannedResponses] = useState<CannedResponse[]>([])
  const [cannedLoaded, setCannedLoaded] = useState(false)
  const [slashQuery, setSlashQuery] = useState<string | null>(null)
  const [slashIndex, setSlashIndex] = useState(0)
  const slashMenuRef = useRef<HTMLDivElement>(null)

  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const imageInputRef = useRef<HTMLInputElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const emojiPickerRef = useRef<HTMLDivElement>(null)
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const schedulePickerRef = useRef<HTMLDivElement>(null)

  // Lazy-load canned responses on first / keypress
  const loadCanned = useCallback(async () => {
    if (cannedLoaded) return
    try {
      const data = await cannedResponsesApi.list()
      setCannedResponses(data)
    } catch {
      // silently ignore
    } finally {
      setCannedLoaded(true)
    }
  }, [cannedLoaded])

  const slashMatches = slashQuery !== null
    ? cannedResponses.filter(r =>
        r.shortcut.startsWith(slashQuery) ||
        r.title.toLowerCase().includes(slashQuery.toLowerCase())
      )
    : []

  const applyCannedResponse = (item: CannedResponse) => {
    const resolved = resolveCannedVars(item.content, cannedVars || {})
    setText(resolved)
    setSlashQuery(null)
    setTimeout(() => textareaRef.current?.focus(), 0)
  }

  // Auto-resize del textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`
    }
  }, [text])

  // Permite autocompletar el borrador desde contenedores externos (ej: HITL resume).
  useEffect(() => {
    const normalized = typeof prefillText === 'string' ? prefillText.trim() : ''
    if (!normalized) return
    setText(normalized.slice(0, MAX_MESSAGE_LENGTH))
    setTimeout(() => textareaRef.current?.focus({ preventScroll: true }), 0)
  }, [prefillText, prefillVersion])

  useEffect(() => {
    if (focusTrigger && focusTrigger > 0) {
      setTimeout(() => textareaRef.current?.focus({ preventScroll: true }), 0)
    }
  }, [focusTrigger])

  // Manejar typing indicators
  useEffect(() => {
    if (!onTyping) return

    if (text.length > 0) {
      // Usuario está escribiendo
      onTyping(true)

      // Limpiar timeout anterior
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current)
      }

      // Después de 3 segundos sin escribir, enviar isTyping=false
      typingTimeoutRef.current = setTimeout(() => {
        onTyping(false)
      }, 3000)
    } else {
      // Campo vacío, no está escribiendo
      onTyping(false)
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current)
      }
    }

    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current)
      }
    }
  }, [text, onTyping])

  // Cerrar emoji picker al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        emojiPickerRef.current &&
        !emojiPickerRef.current.contains(event.target as Node)
      ) {
        setShowEmojiPicker(false)
      }
    }

    if (showEmojiPicker) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showEmojiPicker])

  // Cerrar schedule picker al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        schedulePickerRef.current &&
        !schedulePickerRef.current.contains(event.target as Node)
      ) {
        setShowSchedulePicker(false)
      }
    }

    if (showSchedulePicker) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showSchedulePicker])

  // Convertir archivo a base64
  const fileToBase64 = (file: File): Promise<{ dataUrl: string; base64: string }> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onloadend = () => {
        const result = reader.result as string
        const parts = result.split(',')
        const base64 = parts.length > 1 ? parts[1] : parts[0]
        resolve({ dataUrl: result, base64 })
      }
      reader.onerror = reject
      reader.readAsDataURL(file)
    })
  }

  // Manejar selección de imagen
  const handleImageSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    try {
      const { dataUrl, base64 } = await fileToBase64(file)

      const attachment: MessageAttachment = {
        id: `img-${Date.now()}`,
        type: 'image',
        file,
        preview: dataUrl,
        base64,
      }

      setAttachments([attachment]) // Solo una imagen a la vez (como WhatsApp)
    } catch (error) {
      console.error('Error al procesar imagen:', error)
    }

    // Limpiar input para permitir seleccionar el mismo archivo nuevamente
    event.target.value = ''
  }

  // Manejar selección de archivo
  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    try {
      const { base64 } = await fileToBase64(file)

      const attachment: MessageAttachment = {
        id: `file-${Date.now()}`,
        type: 'file',
        file,
        base64,
      }

      setAttachments([attachment]) // Solo un archivo a la vez
    } catch (error) {
      console.error('Error al procesar archivo:', error)
    }

    event.target.value = ''
  }

  // Remover adjunto
  const handleRemoveAttachment = (id: string) => {
    setAttachments((prev) => prev.filter((att) => att.id !== id))
  }

  // Manejar clic en emoji
  const handleEmojiClick = (emojiData: EmojiClickData) => {
    setText((prev) => prev + emojiData.emoji)
    setShowEmojiPicker(false)
    textareaRef.current?.focus()
  }

  // Enviar mensaje
  const handleSend = async () => {
    if (isSending || disabled) return

    const trimmedText = text.slice(0, MAX_MESSAGE_LENGTH).trim()
    const scheduledDate = scheduledAt ? new Date(scheduledAt) : null

    // Si hay adjunto, enviar con caption opcional (no se permite programar adjuntos)
    if (attachments.length > 0) {
      const attachment = attachments[0]

      setIsSending(true)

      try {
        if (attachment.type === 'image' && attachment.base64) {
          await onSendImage(attachment.file, attachment.base64, trimmedText || undefined)
        } else if (attachment.type === 'file' && attachment.base64) {
          await onSendFile(attachment.file, attachment.base64, trimmedText || undefined)
        }

        // Limpiar después de enviar
        setText('')
        setAttachments([])
        setScheduledAt('')
        setShowSchedulePicker(false)
      } catch (error) {
        console.error('Error al enviar mensaje con adjunto:', error)
      } finally {
        setIsSending(false)
      }

      return
    }

    // Si solo hay texto
    if (trimmedText) {
      setIsSending(true)

      try {
        // Pass scheduledAt if a date is selected
        await onSendText(trimmedText, scheduledDate ? scheduledDate.toISOString() : undefined)
        setText('')
        setScheduledAt('')
        setShowSchedulePicker(false)
      } catch (error) {
        console.error('Error al enviar mensaje de texto:', error)
      } finally {
        setIsSending(false)
      }
    }
  }

  // Detect slash command in textarea
  const handleTextChange = (value: string) => {
    setText(value.slice(0, MAX_MESSAGE_LENGTH))
    // Detect /query pattern at start or after space
    const slashMatch = value.match(/(?:^|\s)\/(\w*)$/)
    if (slashMatch) {
      loadCanned()
      setSlashQuery(slashMatch[1])
      setSlashIndex(0)
    } else {
      setSlashQuery(null)
    }
  }

  // Manejar teclas
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Navigate slash menu
    if (slashQuery !== null && slashMatches.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setSlashIndex(i => Math.min(i + 1, slashMatches.length - 1))
        return
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        setSlashIndex(i => Math.max(i - 1, 0))
        return
      }
      if (e.key === 'Enter') {
        e.preventDefault()
        applyCannedResponse(slashMatches[slashIndex])
        return
      }
      if (e.key === 'Escape') {
        e.preventDefault()
        setSlashQuery(null)
        return
      }
    }
    if ((e.key === 'Enter' && !e.shiftKey) || ((e.ctrlKey || e.metaKey) && e.key === 'Enter')) {
      e.preventDefault()
      handleSend()
    }
  }

  const canSend = !isSending && !disabled && (text.trim().length > 0 || attachments.length > 0)

  return (
    <div className="bg-[#F0F2F5] border-t border-gray-200 px-2 sm:px-4 py-2 safe-area-bottom">
      {/* Preview de adjuntos */}
      {attachments.length > 0 && (
        <div className="mb-2 bg-white rounded-lg p-2 sm:p-3 shadow-sm">
          {attachments.map((attachment) => (
            <div key={attachment.id} className="flex items-start gap-2 sm:gap-3">
              {attachment.type === 'image' && attachment.preview ? (
                <div className="relative">
                  <img
                    src={attachment.preview}
                    alt={attachment.file.name}
                    className="w-16 h-16 sm:w-20 sm:h-20 object-cover rounded-md"
                  />
                  <button
                    type="button"
                    onClick={() => handleRemoveAttachment(attachment.id)}
                    className="absolute -top-1.5 -right-1.5 sm:-top-2 sm:-right-2 bg-red-500 text-white rounded-full p-0.5 sm:p-1 hover:bg-red-600 transition-colors touch-target"
                    disabled={isSending}
                    aria-label="Eliminar imagen"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <div className="bg-gray-100 p-1.5 sm:p-2 rounded-md flex-shrink-0">
                    <Paperclip className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs sm:text-sm font-medium text-gray-900 truncate">
                      {attachment.file.name}
                    </p>
                    <p className="text-[10px] sm:text-xs text-gray-500">
                      {(attachment.file.size / 1024).toFixed(1)} KB
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemoveAttachment(attachment.id)}
                    className="text-red-500 hover:text-red-600 transition-colors p-1.5 touch-target flex-shrink-0"
                    disabled={isSending}
                    aria-label="Eliminar archivo"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Slash command dropdown */}
      {slashQuery !== null && slashMatches.length > 0 && (
        <div
          ref={slashMenuRef}
          className="mb-2 bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden max-h-56 overflow-y-auto"
        >
          {slashMatches.map((item, idx) => (
            <button
              key={item.id}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); applyCannedResponse(item) }}
              className={`w-full flex items-start gap-3 px-4 py-2.5 text-left transition-colors ${idx === slashIndex ? 'bg-[#075f55]/10' : 'hover:bg-slate-50'}`}
            >
              <span className="flex-shrink-0 mt-0.5 inline-flex items-center px-2 py-0.5 bg-[#075f55]/10 text-[#075f55] rounded text-xs font-bold font-mono">
                /{item.shortcut}
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-slate-900 text-sm font-semibold truncate">{item.title}</p>
                <p className="text-slate-500 text-xs truncate">{item.content}</p>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Input principal */}
      <div className="flex items-end gap-1.5 sm:gap-2">
        {/* Contenedor de íconos y textarea */}
        <div className="flex-1 bg-white rounded-xl sm:rounded-lg shadow-sm flex items-end min-w-0">
          {/* Íconos de la izquierda - Ocultos en móvil muy pequeño */}
          <div className="hidden xs:flex items-center gap-0.5 sm:gap-1 px-1 sm:px-2 pb-1.5 sm:pb-2 flex-shrink-0">
            {/* Emoji picker */}
            <div className="relative" ref={emojiPickerRef}>
              <button
                type="button"
                onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                className="p-1.5 sm:p-2 text-gray-500 hover:text-gray-700 active:text-[#075f55] transition-colors rounded-full hover:bg-gray-100 touch-target"
                disabled={disabled || isSending}
                title="Emojis"
                aria-label="Abrir selector de emojis"
              >
                <Smile className="w-5 h-5" />
              </button>

              {showEmojiPicker && (
                <div className="absolute bottom-12 left-0 z-50 shadow-xl rounded-lg overflow-hidden">
                  <EmojiPicker
                    onEmojiClick={handleEmojiClick}
                    width={280}
                    height={350}
                  />
                </div>
              )}
            </div>

            {/* Adjuntar archivo */}
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="p-1.5 sm:p-2 text-gray-500 hover:text-gray-700 active:text-[#075f55] transition-colors rounded-full hover:bg-gray-100 touch-target"
              disabled={disabled || isSending || attachments.length > 0}
              title="Adjuntar archivo"
              aria-label="Adjuntar archivo"
            >
              <Paperclip className="w-5 h-5" />
            </button>
            <input
              ref={fileInputRef}
              type="file"
              onChange={handleFileSelect}
              className="hidden"
              disabled={disabled || isSending}
            />

            {/* Adjuntar imagen */}
            <button
              type="button"
              onClick={() => imageInputRef.current?.click()}
              className="p-1.5 sm:p-2 text-gray-500 hover:text-gray-700 active:text-[#075f55] transition-colors rounded-full hover:bg-gray-100 touch-target"
              disabled={disabled || isSending || attachments.length > 0}
              title="Adjuntar imagen"
              aria-label="Adjuntar imagen"
            >
              <ImageIcon className="w-5 h-5" />
            </button>
            <input
              ref={imageInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageSelect}
              className="hidden"
              disabled={disabled || isSending}
            />
          </div>

          {/* Textarea */}
          <textarea
            ref={textareaRef}
            value={text}
            onChange={(e) => handleTextChange(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={
              attachments.length > 0
                ? attachments[0].type === 'image'
                  ? 'Pie de foto...'
                  : 'Mensaje...'
                : placeholder
            }
            disabled={disabled || isSending}
            rows={1}
            maxLength={MAX_MESSAGE_LENGTH}
            className="flex-1 px-2 sm:px-3 py-2.5 sm:py-2 bg-transparent border-none focus:outline-none resize-none max-h-[100px] sm:max-h-[120px] overflow-y-auto text-[16px] sm:text-[15px] placeholder:text-gray-400 min-w-0"
            style={{ minHeight: '44px' }}
            aria-label="Escribe un mensaje"
          />

          {/* Íconos en móvil pequeño - dentro del input */}
          <div className="flex xs:hidden items-center gap-0.5 pr-1 pb-1.5 flex-shrink-0">
            <button
              type="button"
              onClick={() => imageInputRef.current?.click()}
              className="p-1.5 text-gray-500 hover:text-gray-700 active:text-[#075f55] transition-colors rounded-full"
              disabled={disabled || isSending || attachments.length > 0}
              aria-label="Adjuntar"
            >
              <ImageIcon className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Botón de programar mensaje - Desktop */}
        <div className="relative hidden sm:block" ref={schedulePickerRef}>
          <button
            type="button"
            onClick={() => setShowSchedulePicker(!showSchedulePicker)}
            className={`p-2.5 sm:p-3 rounded-full transition-all flex-shrink-0 touch-target ${
              scheduledAt || showSchedulePicker
                ? 'bg-blue-500 text-white'
                : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
            }`}
            disabled={disabled || isSending || attachments.length > 0}
            title="Programar mensaje"
            aria-label="Programar mensaje para más tarde"
          >
            <Clock className="w-5 h-5" />
          </button>

          {/* Schedule Picker Dropdown */}
          {showSchedulePicker && (
            <div className="absolute bottom-14 right-0 bg-white rounded-lg shadow-xl border border-gray-200 p-3 w-64 z-50">
              <div className="text-sm font-medium text-gray-700 mb-2">
                Enviar más tarde
              </div>
              <input
                type="datetime-local"
                value={scheduledAt}
                onChange={(e) => setScheduledAt(e.target.value)}
                min={new Date().toISOString().slice(0, 16)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              {scheduledAt && (
                <div className="mt-2 flex gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setScheduledAt('')
                      setShowSchedulePicker(false)
                    }}
                    className="flex-1 px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-100 rounded-lg"
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowSchedulePicker(false)}
                    className="flex-1 px-3 py-1.5 text-xs bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                  >
                    Confirmar
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Botón de enviar */}
        <button
          type="button"
          onClick={handleSend}
          disabled={!canSend}
          className={`p-2.5 sm:p-3 rounded-full transition-all flex-shrink-0 touch-target ${
            canSend
              ? 'bg-[#00A884] hover:bg-[#008F6F] active:bg-[#007A5E] text-white shadow-md hover:shadow-lg active:scale-95'
              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
          }`}
          title={canSend ? (scheduledAt ? 'Programar mensaje' : 'Enviar') : 'Escribe un mensaje o adjunta un archivo'}
          aria-label={canSend ? (scheduledAt ? 'Programar mensaje' : 'Enviar mensaje') : 'Escribe un mensaje primero'}
        >
          {isSending ? (
            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : scheduledAt ? (
            <Clock className="w-5 h-5" />
          ) : (
            <Send className="w-5 h-5" />
          )}
        </button>
      </div>

      {/* Hint de atajos - Solo en desktop */}
      <div className="hidden sm:block mt-1 px-1">
        <p className="text-[11px] text-gray-400">
          <kbd className="px-1 py-0.5 bg-gray-100 rounded text-[10px]">Enter</kbd> enviar •{' '}
          <kbd className="px-1 py-0.5 bg-gray-100 rounded text-[10px]">Shift+Enter</kbd> nueva línea •{' '}
          <kbd className="px-1 py-0.5 bg-gray-100 rounded text-[10px]">/</kbd> respuestas rápidas
        </p>
      </div>
    </div>
  )
}
